import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { schoolService } from '../services/schoolService';
import { statisticsService, QuestionnaireStatisticsOverview } from '../services/statisticsService';
import { School } from '../types';
import { BarChartIcon, FileTextIcon } from '../components/Icons';

const SchoolDataPage: React.FC = () => {
  const navigate = useNavigate();
  const { schoolId: schoolIdParam } = useParams<{ schoolId: string }>();
  const schoolId = parseInt(schoolIdParam || '0');

  const [school, setSchool] = useState<School | null>(null);
  const [questionnaires, setQuestionnaires] = useState<QuestionnaireStatisticsOverview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (schoolId) {
      loadData();
    }
  }, [schoolId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [schoolData, questionnaireData] = await Promise.all([
        schoolService.getSchool(schoolId),
        statisticsService.getQuestionnairesOverview(schoolId),
      ]);
      setSchool(schoolData);
      setQuestionnaires(questionnaireData);
    } catch (err: any) {
      setError(err.response?.data?.message || '加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  const totalAnswers = questionnaires.reduce((sum, q) => sum + q.totalAnswers, 0);

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/schools')}
          className="text-sm text-on-surface-variant hover:text-on-surface mb-4 flex items-center space-x-1"
        >
          <span>←</span>
          <span>返回学校列表</span>
        </button>
        <h1 className="text-2xl font-bold text-on-surface font-headline">
          {school?.name} - 数据管理
        </h1>
        <p className="text-sm text-on-surface-variant font-body mt-1">查看该学校的问卷回答情况与统计分析</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl mb-6">
          {error}
        </div>
      )}

      {/* 概览卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-surface-container-lowest rounded-3xl shadow-soft p-5">
          <div className="text-sm text-on-surface-variant font-label mb-1">问卷数量</div>
          <div className="text-2xl font-bold text-on-surface font-headline">{questionnaires.length}</div>
        </div>
        <div className="bg-surface-container-lowest rounded-3xl shadow-soft p-5">
          <div className="text-sm text-on-surface-variant font-label mb-1">总答题人次</div>
          <div className="text-2xl font-bold text-primary-600 font-headline">{totalAnswers}</div>
        </div>
        <div className="bg-surface-container-lowest rounded-3xl shadow-soft p-5">
          <div className="text-sm text-on-surface-variant font-label mb-1">学校</div>
          <div className="text-2xl font-bold text-on-surface font-headline">{school?.name || '-'}</div>
        </div>
      </div>

      {/* 问卷列表 */}
      <div className="bg-surface-container-lowest rounded-3xl shadow-soft overflow-hidden">
        <div className="px-6 py-4" style={{ borderBottom: '1px solid rgba(171, 179, 183, 0.2)' }}>
          <h2 className="text-lg font-semibold text-on-surface font-headline">问卷列表</h2>
          <p className="text-sm text-on-surface-variant font-body mt-0.5">该学校有答题数据的问卷</p>
        </div>

        {loading ? (
          <div className="p-8 text-center text-on-surface-variant font-body">加载中...</div>
        ) : questionnaires.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-on-surface-variant mb-4 flex justify-center">
              <FileTextIcon size={48} />
            </div>
            <p className="text-on-surface-variant font-body">暂无答题数据</p>
            <p className="text-sm text-on-surface-variant font-label mt-1">该学校尚未有学生提交问卷答案</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {questionnaires.map((q) => (
              <div
                key={q.questionnaireId}
                onClick={() => navigate(`/questionnaires/${q.questionnaireId}/statistics`, { state: { schoolId, from: 'school-data' } })}
                className="px-6 py-4 flex items-center justify-between hover:bg-surface-container-low cursor-pointer transition-colors"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <BarChartIcon size={20} className="text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-on-surface font-body">{q.questionnaireTitle}</h3>
                    <div className="flex items-center space-x-3 mt-1">
                      <span className="text-xs text-on-surface-variant font-label">
                        {q.totalAnswers} 人已答
                      </span>
                      {q.lastCalculatedAt && (
                        <span className="text-xs text-on-surface-variant font-label">
                          统计时间: {q.lastCalculatedAt}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium font-label ${
                    q.calculationStatus === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                    q.calculationStatus === 'CALCULATING' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-surface-container-high text-on-surface-variant'
                  }`}>
                    {q.calculationStatus === 'COMPLETED' ? '已统计' :
                     q.calculationStatus === 'CALCULATING' ? '计算中' : '待统计'}
                  </span>
                  <span className="text-on-surface-variant text-sm">→</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SchoolDataPage;
